import { useCallback, useMemo } from "react";

import {
  type ClaimHistoryItem,
  type Payout,
  type PayoutQueryParams,
  PayoutQueryParamsSchema,
  payoutsEndpoints,
} from "../../../api";
import { useStrapiList, type UseStrapiListOptions } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

interface PayoutFilters {
  showFailed?: boolean;
  pm_user?: unknown;
  [key: string]: unknown;
}

interface StrapiFilter {
  pm_status?: { $in: string[] };
  pm_user?: unknown;
  [key: string]: unknown;
}

interface UsePayoutsProps extends Omit<UseStrapiListOptions, "filters"> {
  mine?: boolean;
  filters?: PayoutFilters;
}

/**
 * Convert raw Payout (with pm_* fields) to ClaimHistoryItem format
 */
function payoutToClaimHistoryItem(payout: Payout): ClaimHistoryItem {
  return {
    id: payout.id,
    _source: "payout",
    invoiceNumber: payout.pm_invoiceNumber ?? null,
    amount: payout.pm_amount,
    currency: payout.pm_currency,
    status: payout.pm_status,
    createdAt: payout.createdAt ?? null,
    updatedAt: payout.updatedAt ?? null,
  };
}

/**
 * Transform user-friendly filter keys to Strapi filter format
 */
const transformFilters = (filters: PayoutFilters | undefined): StrapiFilter | undefined => {
  if (!filters) return undefined;

  const result: StrapiFilter = {};

  for (const [key, val] of Object.entries(filters)) {
    if (val === undefined || val === null || val === "") continue;

    switch (key) {
      case "showFailed":
        // When showFailed is false, filter out failed payouts
        if (!val) {
          result.pm_status = { $in: ["pending", "processing", "processed"] };
        }
        // When showFailed is true, don't filter by status (show all)
        break;
      case "pm_user":
        result.pm_user = val;
        break;
      default:
        result[key] = val;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

// autoLoad, sort, pagination, filters, populate
const usePayouts = (props: UsePayoutsProps) => {
  const { filters: rawFilters, mine, ...otherProps } = props || {};

  // Transform filters to Strapi format
  const filters = transformFilters(rawFilters);

  // Stable load function - only changes when mine changes
  const isMine = !!mine;
  const load = useCallback(
    (params: Record<string, unknown>) => {
      const parsed = PayoutQueryParamsSchema.parse(params);
      return loadPayouts(parsed, isMine);
    },
    [isMine]
  );

  // Stable error handler
  const onLoadError = useCallback((err: unknown) => {
    handleError(getErrorMessage(err, "Payouts loading failed"));
  }, []);

  const {
    items: rawPayouts,
    refresh,
    isLoading,
    ...other
  } = useStrapiList<Payout>({
    ...otherProps,
    filters,
    load,
    onLoadError,
  });

  // Transform raw payouts to ClaimHistoryItem format
  const claims = useMemo(() => (rawPayouts || []).map(payoutToClaimHistoryItem), [rawPayouts]);

  return {
    claims,
    refresh,
    isLoading,
    ...other,
  };
};

export const loadPayouts = async (params: PayoutQueryParams, mine?: boolean) => {
  try {
    return mine ? payoutsEndpoints.getMyPayouts(params) : payoutsEndpoints.getPayouts(params);
  } catch (err: unknown) {
    handleError(getErrorMessage(err, "Payouts loading failed"));
  }
};

export default usePayouts;
